import { IClosingFileResponse } from '@/interfaces/IClosingFileResponse'
import { downloadBlob, getFilenameFromContentDisposition, parseBlobErrorMessage } from '@/utils/BlobResponseHelper'
import axios, { AxiosResponse } from 'axios'
import { AttaHttpClientFactory } from './http/AttaHttpClientFactory'
import {
	IApiResponse,
	IClosing,
	IClosingConferenceDTO,
	IClosingFile,
	IClosingFilterPaginationModel,
	IPeriodListResponse,
	IProposta,
	IReconciliationResponseCollection
} from '@/interfaces'

const http = AttaHttpClientFactory.create(import.meta.env.VUE_APP_API_ATTA_SERVICES)

const DEFAULT_CLOSING_EXPORT_FILENAME = 'relatorio-consolidado.csv'

export class ClosingServices {
	/*
	 * Get closing periods
	 */
	static async getClosingPeriods() {
		var response = await http.get<IApiResponse<Array<IPeriodListResponse>>>('/closing/periods')
		return response.data.result
	}

	/**
	 * Get list of closing files by closingId
	 */
	static async getClosingFiles(closingId: string) {
		var response = await http.get<Array<IClosingFileResponse>>(`/closing/${closingId}/files`)
		return response.data
	}

	/**
	 * Upload closing file from specific bank
	 * @param bankId
	 * @returns
	 */
	static async uploadClosingFile(
		file: any,
		bankId: number,
		comments: string,
		createClosing: boolean = true,
		proposalId: number | null = null
	) {
		let formData = new FormData()
		formData.append('file', file)
		formData.append('bank', bankId.toString())
		formData.append('createClosing', createClosing.toString())
		formData.append('comments', comments)
		if (proposalId) formData.append('proposalId', proposalId.toString())

		var response = await http.post<IApiResponse<IClosingFile>>(`/closing/file/upload/${bankId}`, formData, {
			headers: {
				'Content-Type': 'multipart/form-data'
			}
		})
		return response.data
	}

	/**
	 * Get proposals without a closing file
	 * @param bankId
	 * @param partnerId
	 * @param franchiseId
	 * @returns
	 */
	static async getProposalsWithoutClosingFile(bankId: number, franchiseId: number) {
		const response = await http.get<IApiResponse<Array<IProposta>>>(
			`/reconciliation/proposals-to-reconciliation/${bankId}?franchiseId=${franchiseId}`
		)
		return response.data.result
	}

	/**
	 * Get reconciliationItems paginated
	 * @returns
	 */
	static async getReconciliationItemsPaginated(params: IClosingFilterPaginationModel) {
		var response = await http.get<IApiResponse<IReconciliationResponseCollection>>('/reconciliation/current-closing/reconciliation-items', {
			params
		})

		return response.data.result
	}

	/**
	 * Get if the bank already has a file at the current closing
	 * @returns
	 */
	static async getBankHasClosingFile(bankId: number) {
		var response = await http.get<IApiResponse<boolean>>(`/closing/file/has-file/${bankId}`)
		return response.data.result
	}

	/**
	 * Compare reconciliations and proposals to get validated or incorrect ones
	 * @returns
	 */
	static async getClosingConferenceSummary(bankId: number) {
		var response = await http.get<IApiResponse<IClosingConferenceDTO>>(`/reconciliation/proposal/compare/${bankId}`)
		return response.data.result
	}

	/**
	 * Update reconciliation status
	 * @param bankId
	 * @returns
	 */
	static async updateReconciliationStatus(
		reconciliationId: string,
		status: number,
		observation: string = '',
		idIsolve: number | null = null
	) {
		var response = await http.patch<IApiResponse<boolean>>(`/reconciliation/${reconciliationId}/status`, {
			status,
			observation,
			idIsolve
		})
		return response.data
	}

	/**
	 * Revalidate proposal reconciliation
	 * @param reconciliationId
	 * @param bankId
	 * @returns
	 */
	static async revalidateReconciliation(reconciliationId: string, bank: number) {
		var response = await http.patch<IApiResponse<boolean>>(`/reconciliation/${reconciliationId}/revalidate/${bank}`)
		return response.data
	}

	/**
	 * Finish closing
	 * @param closingId
	 * @returns
	 */
	static async finishClosing(closingId: string) {
		var response = await http.post<IApiResponse<boolean>>(`/closing/${closingId}/finish`)
		return response.data
	}

	/**
	 * Get last completed closing
	 * @returns Array<IClosing>
	 */
	public static async getLastCompletedClosing() {
		const response = await http.get<IApiResponse<IClosing>>(`/closing/get-last-completed`)
		return response.data
	}

	/**
	 * Delete closing file
	 * @param closingId
	 * @param fileId
	 * @returns
	 */
	static async deleteClosingFile(closingId: string, fileId: string) {
		var response = await http.delete<IApiResponse<boolean>>(`/closing/${closingId}/file/${fileId}`)
		return response.data
	}

	static async downloadClosingReport(closingId: string): Promise<void> {
		try {
			const response = await http.get<Blob>(`/closing/${closingId}/export`, {
				responseType: 'blob'
			})

			await this.handleClosingExportResponse(response)
		} catch (error) {
			if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
				const message = await parseBlobErrorMessage(error.response.data)
				throw new Error(message)
			}

			throw error
		}
	}

	private static async handleClosingExportResponse(response: AxiosResponse<Blob>): Promise<void> {
		const blob = response.data
		const contentType = String(response.headers['content-type'] ?? '')

		if (contentType.includes('application/json') || contentType.includes('text/json')) {
			const message = await parseBlobErrorMessage(blob)
			throw new Error(message)
		}

		const fileName =
			getFilenameFromContentDisposition(response.headers['content-disposition']) ??
			getFilenameFromContentDisposition(response.headers['Content-Disposition']) ??
			DEFAULT_CLOSING_EXPORT_FILENAME

		downloadBlob(blob, fileName)
	}
}
